import type { Collector } from "./types";
import { isBrowser } from "./types";

export interface GlInfo {
  vendor?: string;
  renderer?: string;
  glHash?: string;
}

export const webglCollector: Collector<GlInfo> = {
  name: "webgl",
  version: 1,
  timeoutMs: 500,
  collect() {
    return glInfo();
  },
};

function glInfo(): GlInfo {
  if (!isBrowser) return {};
  try {
    const c = document.createElement("canvas");
    const gl = (c.getContext("webgl") ||
      c.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return {};
    const ext = gl.getExtension("WEBGL_debug_renderer_info") as any;
    const vendor = ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : undefined;
    const renderer = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : undefined;
    const s = `${vendor || ""}|${renderer || ""}`;
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0;
    return { vendor, renderer, glHash: h.toString(16) };
  } catch {
    return {};
  }
}

export interface GpuNormalizedDisplay {
  isNormalized: boolean;
  primaryW?: number;
  primaryH?: number;
  maxTexture?: number;
  depth?: number;
  dpr?: number;
  gpuRenderer?: string;
  gpuVendor?: string;
}

/**
 * GPU-based display normalization using WebGL renderer info — gives
 * monitor-independent fingerprint inputs (max viewport / max texture
 * are GPU characteristics, not display-attached).
 */
export function getGPUNormalizedDisplay(): GpuNormalizedDisplay {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return { isNormalized: false };

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info") as any;
    if (!debugInfo) return { isNormalized: false };

    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "";
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || "";

    const maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as number[];
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;

    return {
      isNormalized: true,
      primaryW: maxViewport[0],
      primaryH: maxViewport[1],
      maxTexture: maxTextureSize,
      depth: screen.colorDepth,
      dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
      gpuRenderer: renderer.split(" ").slice(0, 3).join(" "),
      gpuVendor: vendor.split(" ")[0],
    };
  } catch {
    return { isNormalized: false };
  }
}
